// ============================================================================
// Time & Labor — what an account manager sees.
//
// Hours and My night managers are the account manager's own tabs; which other
// Time & Labor tabs sit beside them is `timeTabsFor()` in time-console.tsx
// (Ben, 2026-10-26, items #981 and #982). The payroll tabs stay with HR,
// finance and executives.
//
// ⛔ NO RATE, WAGE, AMOUNT, COST OR MARGIN APPEARS ON ANY SCREEN IN THIS FILE,
// and `am-views.no-money.test.ts` asserts it against the source. An account
// manager sees hours and where somebody was, never what they are paid.
//
// ⛔ A night manager is a POSITION filled by a person (Ben, 2026-09-21). Every
// screen leads with the position and names the person second, the same rule the
// rest of the app uses for ownership.
// ============================================================================

import { isAmRole, seesAllNightManagerBuildingTime, setsAnyNightManagerRecording } from '../../shared/role-gates';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTimeTabsNav } from './time-console';
import { Button, PageHeader, TabBar } from '../../shared/components';
import { useAuth } from '../../shared/auth';
import { errorMessage } from '../../shared/errors';
import { useTimeHoursScope } from './use-hours-scope';
import {
  confirmFullAutoclose,
  issueWorkerPin,
  setNightManagerRecording,
  useHours,
  useNightManagers,
  useNights,
  useWorkerOfException,
} from './hooks/use-am-data';
import { PinShown } from './pin-shown';
import { BuildingTimePanel } from './building-time-panel';
import { FAR_PUNCH_M } from './types-am';
import type { NightRow, PunchPoint, WorkerLang } from './types-am';
import { formatSiteTime } from '../../lib/siteTime';


// Which tabs an account manager sees is decided in one place, `timeTabsFor()`
// in time-console.tsx, for the HR console and the AM side alike (Ben,
// 2026-10-26: the national AM sees every tab the database lets that seat use;
// an account manager gains Timecards to record and correct a shift).
// Client views became Hours links (Ben, 2026-10-23); /time/client-views forwards there.
export type AmTabId = 'hours' | 'night-managers' | 'hours-links';

export function useAmTabsNav(active: AmTabId) {
  return useTimeTabsNav(active);
}

// ---------------------------------------------------------------------------
// Format helpers — payroll and shifts render in Central on purpose, so a night
// reads the same from any laptop.
// ---------------------------------------------------------------------------
/** ⛔ The worker's clock, labelled — never Kansas City's, never the viewer's
 *  (Ben, 2026-09-24). The zone is the building's market, carried on the row. */
function fmtTime(iso: string | null, tz: string | null): string {
  if (!iso) return '—';
  return formatSiteTime(iso, tz);
}

function fmtDay(d: string): string {
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
  });
}

function today(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  const pad = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Plain words for a punch's location. Never a bare number on its own. */
function locationWords(p: PunchPoint): string {
  if (p.capture_mode === 'supervisor_manual') return 'typed by hand — no location';
  if (p.distance_from_site_m == null) return 'no location recorded';
  const m = Math.round(p.distance_from_site_m);
  const acc = p.gps_accuracy_m != null ? ` · GPS good to ${Math.round(p.gps_accuracy_m)} m` : '';
  const where = p.geofence_result === 'inside' ? 'on site' : 'away from the building';
  return `${where} · ${m} m from the door${acc}`;
}

const EXCEPTION_WORDS: Record<string, string> = {
  missing_punch_out: 'never clocked out',
  daily_ceiling_auto_close: 'auto-closed — confirm',
  negative_duration: 'out before in',
  over_scheduled_hours: 'over the scheduled span',
  self_approval_conflict: 'needs a second approver',
};

function exceptionWords(code: string | null): string | null {
  if (!code) return null;
  return EXCEPTION_WORDS[code] ?? code.replace(/_/g, ' ');
}

// ---------------------------------------------------------------------------
// Small shared bits
// ---------------------------------------------------------------------------
function Pill({ tone, children }: { tone: 'ok' | 'warn' | 'info' | 'bad'; children: React.ReactNode }) {
  const cls = tone === 'ok'
    ? 'border-st-ok-border bg-st-ok-bg text-st-ok-fg'
    : tone === 'warn'
      ? 'border-st-warn-border bg-st-warn-bg text-st-warn-fg'
      : tone === 'bad'
        ? 'border-st-danger-border bg-st-danger-bg text-st-danger-fg'
        : 'border-st-info-border bg-st-info-bg text-st-info-fg';
  return (
    <span className={`inline-block rounded-full border px-2 py-0.5 text-[10.5px] font-semibold ${cls}`}>
      {children}
    </span>
  );
}

function PositionChip({ title, market }: { title: string; market: string | null }) {
  return (
    <span className="inline-block rounded-lg border border-tcs-border-strong bg-tcs-surface px-2.5 py-1">
      <span className="block text-[13px] font-bold text-tcs-text">{title}</span>
      {market && <span className="block text-[11px] text-tcs-text-muted">{market}</span>}
    </span>
  );
}

function Loading() {
  return <div className="mt-4 text-sm text-tcs-text-muted">Loading…</div>;
}

function Failed({ msg }: { msg: string }) {
  return <div className="mt-4 text-sm text-st-danger-fg">{msg}</div>;
}

// ===========================================================================
// HOURS — the landing tab
// ===========================================================================
export function AmHours() {
  const tabsNav = useAmTabsNav('hours');
  const [from, setFrom] = useState(daysAgo(7));
  const [to, setTo] = useState(today());
  const { everyone, setEveryone } = useTimeHoursScope();
  const { rows, loading, error, myCount } = useHours(from, to, everyone);

  const total = useMemo(() => rows.reduce((t, r) => t + r.hours, 0), [rows]);
  const people = useMemo(() => new Set(rows.map((r) => r.worker_id)).size, [rows]);

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <PageHeader
        eyebrow="Time & Labor"
        title="Hours"
        meta="Hours only. Nothing on this screen shows what anyone is paid — that sits with payroll."
      />
      <div className="mt-3"><TabBar {...tabsNav} /></div>

      <section className="mt-5 rounded-xl border border-tcs-border bg-tcs-raised p-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="text-xs font-semibold text-tcs-text-secondary" htmlFor="h-from">From</label>
              <input id="h-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                className="mt-1 block h-9 rounded-lg border border-tcs-border-input bg-tcs-field px-3 text-sm text-tcs-text" />
            </div>
            <div>
              <label className="text-xs font-semibold text-tcs-text-secondary" htmlFor="h-to">Through</label>
              <input id="h-to" type="date" value={to} onChange={(e) => setTo(e.target.value)}
                className="mt-1 block h-9 rounded-lg border border-tcs-border-input bg-tcs-field px-3 text-sm text-tcs-text" />
            </div>
          </div>
          <label className="flex cursor-pointer select-none items-center gap-2">
            <input type="checkbox" checked={everyone} onChange={(e) => setEveryone(e.target.checked)}
              className="h-4 w-4 accent-tcs-text" />
            <span className="text-sm font-semibold text-tcs-text">Show everyone</span>
          </label>
        </div>
        <div className="mt-2 text-xs text-tcs-text-secondary">
          {everyone
            ? <>Showing <b>everyone</b> — every person who punched in this range, not only the {myCount} buildings you manage.</>
            : <>Showing <b>your buildings</b> — the {myCount} where you are the account manager.</>}
        </div>
      </section>

      {loading && <Loading />}
      {error && <Failed msg={error} />}

      {!loading && !error && rows.length === 0 && (
        <div className="mt-4 rounded-xl border border-dashed border-tcs-border-strong px-4 py-8 text-center">
          <div className="text-sm font-semibold text-tcs-text">
            {everyone ? 'Nobody punched in this range.' : 'Nobody at your buildings punched in this range.'}
          </div>
          <div className="mx-auto mt-1 max-w-prose text-xs text-tcs-text-secondary">
            {everyone
              ? 'An ordinary answer, not a fault — the punch clock is still being rolled out building by building.'
              : 'An ordinary answer, not a fault. Turn on “Show everyone” to see the rest of the book.'}
          </div>
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <div className="mt-4 overflow-x-auto rounded-xl border border-tcs-border">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-tcs-surface text-left text-[11px] uppercase tracking-wide text-tcs-text-muted">
                <th className="px-3 py-2 font-semibold">Person</th>
                <th className="px-3 py-2 font-semibold">Building</th>
                <th className="px-3 py-2 font-semibold">Night</th>
                <th className="px-3 py-2 font-semibold">In (worker&rsquo;s time)</th>
                <th className="px-3 py-2 font-semibold">Out (worker&rsquo;s time)</th>
                <th className="px-3 py-2 text-right font-semibold">Hours</th>
                <th className="px-3 py-2 font-semibold">Needs attention</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.segment_id} className="border-t border-tcs-border bg-tcs-raised">
                  <td className="px-3 py-2 font-medium text-tcs-text">{r.worker_name}</td>
                  <td className="px-3 py-2 text-tcs-text-secondary">{r.place_display ?? '—'}</td>
                  <td className="px-3 py-2 text-tcs-text-secondary">{fmtDay(r.shift_date)}</td>
                  <td className="px-3 py-2 tabular-nums">{fmtTime(r.started_at, r.site_tz)}</td>
                  <td className="px-3 py-2 tabular-nums">{fmtTime(r.ended_at, r.site_tz)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.hours.toFixed(2)}</td>
                  <td className="px-3 py-2">
                    {r.open_exception
                      ? <Pill tone="warn">{exceptionWords(r.open_exception)}</Pill>
                      : <span className="text-tcs-text-muted">—</span>}
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-tcs-border-strong bg-tcs-surface font-semibold">
                <td className="px-3 py-2">{people} {people === 1 ? 'person' : 'people'}</td>
                <td className="px-3 py-2" colSpan={4} />
                <td className="px-3 py-2 text-right tabular-nums">{total.toFixed(2)}</td>
                <td className="px-3 py-2" />
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// MY NIGHT MANAGERS — list, then one selected
// ===========================================================================

// The clock-in code control (PinShown) lives in ./pin-shown.tsx since 2026-10-08
// (item 818) so the person card shows the identical thing. Verbatim move.

function NightRoute({ night }: { night: NightRow }) {
  const ins = night.punches.filter((p) => p.event_type === 'in');
  const outs = night.punches.filter((p) => p.event_type === 'out');
  const shown = [...ins.slice(0, 1), ...outs.slice(-2)];
  return (
    <div className="mt-2 rounded-xl border border-tcs-border-strong bg-tcs-overlay p-4">
      <div className="text-sm font-bold text-tcs-text">{fmtDay(night.shift_date)} — the detail</div>
      <div className="mt-2 flex flex-wrap items-stretch gap-0">
        {shown.map((p, i) => (
          <div key={p.punch_event_id} className="flex items-stretch">
            {i > 0 && <div className="flex items-center px-2 text-tcs-text-muted">→</div>}
            <div className={`min-w-[168px] rounded-lg border bg-tcs-surface px-3 py-2 ${
              p.capture_mode === 'supervisor_manual' ? 'border-st-warn-border' : 'border-tcs-border'}`}>
              <div className="text-[9.5px] font-bold uppercase tracking-wider text-tcs-text-muted">
                {p.capture_mode === 'supervisor_manual'
                  ? 'Corrected'
                  : p.event_type === 'in' ? 'Clocked in' : 'Clocked out'}
              </div>
              <div className="text-[12.5px] font-semibold leading-tight text-tcs-text">
                {p.place_display ?? '—'}
              </div>
              <div className="text-[11.5px] text-tcs-text-secondary">{fmtTime(p.event_at, night.site_tz)}</div>
              <div className="mt-0.5 text-[10.5px] text-tcs-text-muted">{locationWords(p)}</div>
              {p.void_reason && (
                <div className="mt-0.5 text-[10.5px] italic text-tcs-text-muted">
                  &ldquo;{p.void_reason}&rdquo;
                </div>
              )}
            </div>
          </div>
        ))}
        {shown.length === 0 && (
          <div className="text-xs text-tcs-text-muted">No punches recorded for this night.</div>
        )}
      </div>
      <div className="mt-2 text-[11.5px] text-tcs-text-muted">
        The original punch is still here. A correction is written on top of it and never replaces it.
      </div>
    </div>
  );
}

/**
 * ONE NIGHT NOBODY HAS ANSWERED, pinned above the table with the words of what
 * is wrong and the one action that settles it.
 *
 * This is the other half of the daily note: the note tells the manager, this is
 * where they say what happened. ⛔ Standing by the full time is the only action
 * here — changing the clock times is the correction door, which already exists
 * and already demands a reason.
 */
function UnansweredNight({
  night, name, onSettled,
}: { night: NightRow; name: string; onSettled: () => void }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [asking, setAsking] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  async function confirmFull() {
    if (!night.open_exception_id) return;
    setBusy(true);
    setFailed(null);
    try {
      await confirmFullAutoclose(night.open_exception_id, note);
      onSettled();
    } catch (e) {
      setFailed(errorMessage(e, 'That did not save — try again.'));
    } finally {
      setBusy(false);
    }
  }

  const ceiling = night.open_exception === 'daily_ceiling_auto_close';

  return (
    <div className="rounded-xl border border-st-danger-border bg-st-danger-bg px-4 py-3">
      <div className="text-sm font-bold text-st-danger-fg">
        {name} · {fmtDay(night.shift_date)} · {ceiling ? 'no clock-out' : exceptionWords(night.open_exception)}
      </div>
      <div className="mt-1 text-[12.5px] text-st-danger-fg">
        {night.in_place ?? 'an unrecorded building'} · clocked in {fmtTime(night.started_at, night.site_tz)}
        {ceiling && <> and never clocked out, so the system closed the shift and recorded {night.hours.toFixed(2)} hours</>}.
        {' '}They are asked about it at their own next punch; until they answer it sits here and on the pay run.
      </div>
      {!asking ? (
        <div className="mt-2">
          <Button size="sm" onClick={() => setAsking(true)}>Say what happened</Button>
        </div>
      ) : (
        <div className="mt-2">
          <label className="text-[11px] font-semibold text-st-danger-fg" htmlFor={`note-${night.segment_id}`}>
            What happened? (optional)
          </label>
          <textarea id={`note-${night.segment_id}`} rows={2} value={note}
            onChange={(e) => setNote(e.target.value)}
            className="mt-1 w-full rounded-lg border border-tcs-border-input bg-tcs-field px-3 py-2 text-sm text-tcs-text" />
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Button size="sm" loading={busy} onClick={() => void confirmFull()}>
              They worked the full {night.hours.toFixed(2)} hours
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setAsking(false)}>Cancel</Button>
            <span className="text-[11px] text-st-danger-fg">
              To change the times instead, correct the night on the Timecards screen.
            </span>
          </div>
        </div>
      )}
      {failed && <div className="mt-2 text-xs font-semibold text-st-danger-fg">{failed}</div>}
    </div>
  );
}

function NightManagerDetail({
  workerId, name, title, market, onBack, showBuildingTime, recordsBuildings,
}: {
  workerId: string; name: string; title: string; market: string | null; onBack: () => void;
  /** Superuser, executive, or the employee this position reports to (item #997). */
  showBuildingTime: boolean;
  /** The position's setting: Night + buildings. */
  recordsBuildings: boolean;
}) {
  const { rows, loading, error, reload } = useNights(workerId);
  const [open, setOpen] = useState<string | null>(null);

  /** Anything on this person's nights that is waiting on a human answer. */
  const needsAnswer = useMemo(() => rows.filter((r) => r.open_exception_id), [rows]);

  const totals = useMemo(() => ({
    nights: rows.length,
    hours: rows.reduce((t, r) => t + r.hours, 0),
    buildings: new Set(rows.flatMap((r) => r.punches.map((p) => p.facility_id)).filter(Boolean)).size,
  }), [rows]);

  return (
    <>
      <button type="button" onClick={onBack}
        className="mb-2 text-sm font-semibold text-tcs-accent-ink hover:underline">
        ← All night managers
      </button>
      <div className="rounded-xl border border-tcs-border bg-tcs-raised p-4">
        <div className="flex flex-wrap items-center gap-3">
          <PositionChip title={title} market={market} />
          <span className="text-lg font-bold text-tcs-text">{name}</span>
        </div>
      </div>

      {loading && <Loading />}
      {error && <Failed msg={error} />}

      {!loading && !error && rows.length === 0 && (
        <div className="mt-4 rounded-xl border border-dashed border-tcs-border-strong px-4 py-8 text-center">
          <div className="text-sm font-semibold text-tcs-text">No nights recorded yet.</div>
          <div className="mx-auto mt-1 max-w-prose text-xs text-tcs-text-secondary">
            Nothing will appear here until they start clocking in on our system.
          </div>
        </div>
      )}

      {/* ⛔ WHAT NEEDS AN ANSWER IS PINNED ABOVE THE TABLE, in the words of
          what is wrong — not as a pill in a column somebody has to notice.
          This is the same night the daily note tells the manager about. */}
      {!loading && !error && needsAnswer.length > 0 && (
        <div className="mt-4 flex flex-col gap-2">
          {needsAnswer.map((r) => (
            <UnansweredNight key={r.segment_id} night={r} name={name} onSettled={reload} />
          ))}
        </div>
      )}

      {/* Where the hours went, from the night manager's building check-ins
          (item #997). Hours only; it adds up to the punched hours. */}
      {!loading && !error && showBuildingTime && (
        <BuildingTimePanel workerId={workerId} nights={rows} recordsBuildings={recordsBuildings} />
      )}

      {!loading && !error && rows.length > 0 && (
        <div className="mt-4 overflow-x-auto rounded-xl border border-tcs-border">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-tcs-surface text-left text-[11px] uppercase tracking-wide text-tcs-text-muted">
                <th className="px-3 py-2 font-semibold">Night</th>
                <th className="px-3 py-2 font-semibold">Building</th>
                <th className="px-3 py-2 font-semibold">In (worker&rsquo;s time)</th>
                <th className="px-3 py-2 font-semibold">Out (worker&rsquo;s time)</th>
                <th className="px-3 py-2 text-right font-semibold">Hours</th>
                <th className="px-3 py-2 font-semibold">Location</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <>
                  <tr key={r.segment_id}
                      onClick={() => setOpen(open === r.segment_id ? null : r.segment_id)}
                      className={`cursor-pointer border-t border-tcs-border ${
                        open === r.segment_id ? 'bg-tcs-surface' : 'bg-tcs-raised hover:bg-tcs-surface'}`}>
                    <td className="px-3 py-2 font-medium text-tcs-text">{fmtDay(r.shift_date)}</td>
                    <td className="px-3 py-2 text-tcs-text-secondary">
                      {r.in_place ?? '—'}
                      {r.out_place && r.out_place !== r.in_place && <> → {r.out_place}</>}
                    </td>
                    <td className="px-3 py-2 tabular-nums">{fmtTime(r.started_at, r.site_tz)}</td>
                    <td className="px-3 py-2 tabular-nums">
                      {/* ⛔ An auto-closed night has an END but no clock-out.
                          Printing the ceiling time as if they had punched out
                          is the lie this whole feature exists to stop. */}
                      {r.open_exception === 'daily_ceiling_auto_close'
                        ? <span className="font-semibold text-st-danger-fg">— none —</span>
                        : fmtTime(r.ended_at, r.site_tz)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.hours.toFixed(2)}</td>
                    <td className="space-x-1 px-3 py-2">
                      {r.was_corrected
                        ? <Pill tone="warn">typed by hand</Pill>
                        : <Pill tone="ok">on site</Pill>}
                      {(r.furthest_m ?? 0) > FAR_PUNCH_M && (
                        <Pill tone="bad">{Math.round(r.furthest_m as number)} m out</Pill>
                      )}
                      {r.open_exception && <Pill tone="warn">{exceptionWords(r.open_exception)}</Pill>}
                    </td>
                  </tr>
                  {open === r.segment_id && (
                    <tr key={`${r.segment_id}-d`} className="border-t border-tcs-border bg-tcs-raised">
                      <td colSpan={6} className="px-3 pb-3"><NightRoute night={r} /></td>
                    </tr>
                  )}
                </>
              ))}
              <tr className="border-t-2 border-tcs-border-strong bg-tcs-surface font-semibold">
                <td className="px-3 py-2">{totals.nights} {totals.nights === 1 ? 'night' : 'nights'}</td>
                <td className="px-3 py-2">{totals.buildings} buildings touched</td>
                <td className="px-3 py-2" colSpan={2} />
                <td className="px-3 py-2 text-right tabular-nums">{totals.hours.toFixed(2)}</td>
                <td className="px-3 py-2" />
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/**
 * The AM's setting on a night-manager position (Ben, 2026-10-27): "Night only"
 * (punch in, punch out) or "Night + buildings" (also check in and out of each
 * building during the night). Recording only — it never changes their hours.
 * Changeable by the AM the position reports to, or a superuser; everyone else
 * sees which it is.
 */
function RecordingSwitch({ on, editable, busy, onChange }: {
  on: boolean; editable: boolean; busy: boolean; onChange: (on: boolean) => void;
}) {
  if (!editable) {
    return <span className="text-xs font-semibold text-tcs-text-secondary">{on ? 'Night + buildings' : 'Night only'}</span>;
  }
  const opt = (value: boolean, label: string) => (
    <button type="button" disabled={busy || on === value} aria-pressed={on === value}
      onClick={() => onChange(value)}
      className={`whitespace-nowrap px-2.5 py-1 text-xs font-semibold ${on === value
        ? 'bg-tcs-inverse text-tcs-inverse-ink'
        : 'bg-tcs-raised text-tcs-text-secondary hover:bg-tcs-surface'}`}>
      {label}
    </button>
  );
  return (
    <div role="group" aria-label="How the night is recorded"
      className={`inline-flex overflow-hidden rounded-lg border border-tcs-border-strong ${busy ? 'opacity-60' : ''}`}>
      {opt(false, 'Night only')}
      {opt(true, 'Night + buildings')}
    </div>
  );
}

export function AmNightManagers() {
  const tabsNav = useAmTabsNav('night-managers');
  const { rows, loading, error, reload } = useNightManagers();
  const [selected, setSelected] = useState<string | null>(null);
  // The daily note links here carrying the night it is about, so the manager
  // lands on that person rather than on a list they have to search.
  const [params] = useSearchParams();
  const fromNote = useWorkerOfException(params.get('exception'));
  const [pin, setPin] = useState<{ name: string; code: string; lang: WorkerLang } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const { employee, profile } = useAuth();
  const myEid = employee?.employee_id ?? null;
  /** May this person change how a position's nights are recorded? The
   *  database decides; this only decides whether to offer the switch. */
  const mayChangeRecording = (reportsTo: string | null) =>
    setsAnyNightManagerRecording(profile) || (myEid !== null && reportsTo === myEid);
  const mayReadBuildingTime = (reportsTo: string | null) =>
    seesAllNightManagerBuildingTime(profile) || (myEid !== null && reportsTo === myEid);

  async function onRecording(positionId: string, on: boolean) {
    setBusy(`rec-${positionId}`);
    setActionError(null);
    try {
      await setNightManagerRecording(positionId, on);
      reload();
    } catch (e) {
      setActionError(errorMessage(e, 'Could not change how the nights are recorded.'));
    } finally {
      setBusy(null);
    }
  }

  const chosen = rows.find((r) => r.position_id === selected)
    ?? (fromNote ? rows.find((r) => r.worker_id === fromNote) ?? null : null);

  async function onIssue(positionId: string, workerId: string, name: string, lang: WorkerLang) {
    setBusy(positionId);
    setActionError(null);
    try {
      const code = await issueWorkerPin(workerId);
      setPin({ name, code, lang });
      reload();
    } catch (e) {
      setActionError(errorMessage(e, 'Could not issue a code.'));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <PageHeader
        eyebrow="Time & Labor"
        title="My night managers"
        meta="Hours and where they were. A night manager is a position; the person fills it."
      />
      <div className="mt-3"><TabBar {...tabsNav} /></div>

      {loading && <Loading />}
      {error && <Failed msg={error} />}
      {actionError && <Failed msg={actionError} />}
      {pin && <PinShown name={pin.name} pin={pin.code} lang={pin.lang} onDone={() => setPin(null)} />}

      {!loading && !error && rows.length === 0 && (
        <div className="mt-4 rounded-xl border border-dashed border-tcs-border-strong px-4 py-8 text-center">
          <div className="text-sm font-semibold text-tcs-text">No night-manager position reports to you.</div>
          <div className="mx-auto mt-1 max-w-prose text-xs text-tcs-text-secondary">
            A night manager appears here once a position naming you as their manager is created.
          </div>
        </div>
      )}

      {!loading && !error && rows.length > 0 && !chosen && (
        <div className="mt-4 overflow-x-auto rounded-xl border border-tcs-border">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-tcs-surface text-left text-[11px] uppercase tracking-wide text-tcs-text-muted">
                <th className="px-3 py-2 font-semibold">Position</th>
                <th className="px-3 py-2 font-semibold">Filled by</th>
                <th className="px-3 py-2 font-semibold">On our clock</th>
                <th className="px-3 py-2 font-semibold">How the night is recorded</th>
                <th className="px-3 py-2 text-right font-semibold">Nights</th>
                <th className="px-3 py-2 text-right font-semibold">Hours</th>
                <th className="px-3 py-2 text-right font-semibold">Buildings</th>
                <th className="px-3 py-2 font-semibold">Needs you</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.position_id} className="border-t border-tcs-border bg-tcs-raised">
                  <td className="px-3 py-2"><PositionChip title={r.title} market={r.market_region} /></td>
                  <td className="px-3 py-2 font-semibold text-tcs-text">
                    {r.filled_by_name ?? <span className="font-normal text-tcs-text-muted">nobody yet</span>}
                  </td>
                  <td className="px-3 py-2">
                    {r.has_pin ? <Pill tone="ok">yes</Pill> : <Pill tone="info">not yet</Pill>}
                  </td>
                  <td className="px-3 py-2">
                    <RecordingSwitch
                      on={r.nm_building_checkins}
                      editable={mayChangeRecording(r.reports_to_eid)}
                      busy={busy === `rec-${r.position_id}`}
                      onChange={(on) => void onRecording(r.position_id, on)}
                    />
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.nights || <span className="text-tcs-text-muted">—</span>}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.hours ? r.hours.toFixed(2) : <span className="text-tcs-text-muted">—</span>}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.buildings || <span className="text-tcs-text-muted">—</span>}</td>
                  <td className="space-x-2 whitespace-nowrap px-3 py-2">
                    {!r.has_pin && r.worker_id && (
                      <Button size="sm" loading={busy === r.position_id}
                        onClick={() => void onIssue(r.position_id, r.worker_id as string, r.filled_by_name ?? 'They', r.worker_language)}>
                        Issue code
                      </Button>
                    )}
                    {r.has_pin && r.worker_id && (
                      <Button size="sm" variant="secondary" loading={busy === r.position_id}
                        onClick={() => void onIssue(r.position_id, r.worker_id as string, r.filled_by_name ?? 'They', r.worker_language)}>
                        Reset code
                      </Button>
                    )}
                    {r.nights > 0 && (
                      <Button size="sm" variant="secondary" onClick={() => setSelected(r.position_id)}>
                        Open
                      </Button>
                    )}
                    {!r.worker_id && <span className="text-xs text-tcs-text-muted">not on the punch clock</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-3 py-2 text-[11.5px] text-tcs-text-muted">
            Whoever needs something from you sorts to the top. Open a person to see their nights.
          </div>
        </div>
      )}

      {chosen && chosen.worker_id && (
        <div className="mt-4">
          <NightManagerDetail
            workerId={chosen.worker_id}
            name={chosen.filled_by_name ?? chosen.worker_id}
            title={chosen.title}
            market={chosen.market_region}
            onBack={() => setSelected(null)}
            showBuildingTime={mayReadBuildingTime(chosen.reports_to_eid)}
            recordsBuildings={chosen.nm_building_checkins}
          />
        </div>
      )}
    </div>
  );
}

/** The router entry for `/time`: HR keeps the exception queue, an AM gets Hours. */
export function useIsAmSide(): boolean {
  const { profile } = useAuth();
  const role = profile?.role ?? 'none';
  return isAmRole(role);
}
